/**
 * The Landingpage container
 */
import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { show as showLandingpage } from "../../redux/landingpages";
import Page from "../../components/Page";
import LandingpageContent from "../../components/LandingpageContent";
import LoadingContent from "../../components/LoadingContent";
import cmsBlocksSelectors from "../../redux/cmsblocks/selectors";
import { useSsrEffect } from "@issr/core";
import Status from "../Utils/Status";

/**
 * The `show` container for a Landingpage.
 * Both the component and the connected component are exported for testing purposes.
 */
const Landingpage = ({ match, location }, context) => {
  const landingpageSlug = match?.params?.slug || false;
  const dispatch = useDispatch();

  const {
    landingpage,
    loadingShow,
    productTeasers,
    cmsBlocks,
    errorRedirect,
    language,
  } = useSelector((state) => {
    const { landingpages } = state;
    const showData = landingpages.show || {};
    const selectBlocks = (key) =>
      cmsBlocksSelectors.selectCMSBlocksForPage(
        state,
        showData[key] ? [showData[key]] : []
      );

    return {
      landingpage: showData,
      loadingShow: landingpages.loadingShow,
      errorRedirect: landingpages.errorRedirect,
      productTeasers: cmsBlocksSelectors.selectCMSBlocksForPage(
        state,
        showData.product_teasers || []
      ),
      cmsBlocks: {
        cmsBlock1: selectBlocks("cms_block_1"),
        cmsBlock2: selectBlocks("cms_block_2"),
        cmsBlock3: selectBlocks("cms_block_3"),
      },
      language: state.i18nState.lang,
    };
  });

  const frontload = useCallback(async () => {
    if (
      (!landingpage ||
        landingpage.language !== language ||
        (landingpage.slug !== landingpageSlug &&
          landingpage.requestedSlug !== landingpageSlug)) &&
      !loadingShow
    ) {
      dispatch(showLandingpage(landingpageSlug));
    }
  }, [landingpage, language, loadingShow, landingpageSlug, dispatch]);

  useSsrEffect(frontload, [
    language,
    landingpage,
    loadingShow,
    landingpageSlug,
  ]);

  const pageLoaded =
    landingpage?.language === language && landingpage?.slug === landingpageSlug;

  const {
    title = "Loading...",
    created_at: published = "",
    meta_description: description = "",
    dataLayer = false,
    jsonLd = false,
    meta_keywords: metaKeywords = false,
    canonical_url: canonicalUrl = false,
  } = landingpage || {};

  const presetFromUrl = new URLSearchParams(location.search); // editorType = product
  const meta_image_url =
    landingpage?.hero_image?.length > 0
      ? landingpage?.hero_image[0].url
      : false;
  return (
    <Page
      id="landingpage"
      className="landingpage"
      lang={language}
      title={pageLoaded ? title : "Loading..."}
      image={pageLoaded ? meta_image_url : false}
      jsonLd={pageLoaded ? jsonLd : false}
      canonical_url={pageLoaded ? canonicalUrl : false}
      dataLayer={
        pageLoaded
          ? (() => {
              try {
                return JSON.parse(dataLayer);
              } catch (e) {
                console.error("Failed to parse dataLayer JSON:", e);
                return { error: "undefined" }; // or return an empty object {} or any other default value
              }
            })()
          : false
      }
      keywords={pageLoaded ? metaKeywords : false}
      published={published}
      description={pageLoaded ? description : false}
      pageType="landingpage"
    >
      {errorRedirect && <Status code={404} redirect={errorRedirect} />}
      {loadingShow && <LoadingContent />}
      {!loadingShow && pageLoaded && (
        <LandingpageContent
          landingpage={landingpage}
          productTeasers={productTeasers}
          cmsBlock1={cmsBlocks.cmsBlock1}
          cmsBlock2={cmsBlocks.cmsBlock2}
          cmsBlock3={cmsBlocks.cmsBlock3}
          presetFromUrl={presetFromUrl}
        />
      )}
    </Page>
  );
};

Landingpage.contextTypes = {
  t: PropTypes.func,
};

export default Landingpage;
